import moment from "moment";
import { getLeads } from "../../api/lead";
import { getProperties } from "../../api/agent";
import { LOST_REASONS, withNote, withdrawnReasonLabel } from "../leads/constants/lostReasons";

/**
 * The Prospects board: one stage model for every seller lead, from the first
 * enquiry to the sale (Christian Gravias, Monday 12988442847). The design asks
 * for "the same five words used everywhere in the product (kanban, dashboard,
 * lead detail), so an agent never has to translate between 'Seller Lead',
 * 'Off Market Pipeline' and 'Appraisal' meaning the same thing."
 *
 * Each stage is read off the record the CRM actually keeps for it, not off
 * Lead.stage. Nothing in the seller workflow moves Lead.stage after a lead
 * arrives (Called, Add as Appraisal, Listed and Lost all write other fields),
 * so a board built on it put every lead in New forever.
 *
 *   New        seller leads in the holding area that nobody has called yet
 *   Connected  seller leads in the holding area the agent has pressed Called on
 *   Long Term  seller leads in the holding area the agent parked as not
 *              selling soon (Lead.long_term_at), called or not
 *   Appraisal  appraisals on the Off Market and On Market boards
 *   Listed     won, status 'Listed': in My Listings, not on the portals yet
 *   For Sale   live sale listings (on or off market, not withdrawn, not sold)
 *   Sold       sales settled in the last SOLD_WINDOW_DAYS days
 *   Lost       seller leads and appraisals the agent marked Lost, less the
 *              ones cleared with the card's X (the record and its lost stats
 *              stay; only the card goes)
 *
 * Listed and For Sale were one column until Christian Gravias, Monday
 * 13177615952 (9 Oct 2026): "I clicked Listed on a property ... it went as
 * listed in my listed but the tile didnt move to listed in the prospects tab.
 * I think we need to create a For Sale tab ... Please also add a LOST red tile
 * on the right of sold."
 *
 * The Dashboard's Pipeline Snapshot reads the same loader, so the numbers on
 * the home page and the columns on this board cannot disagree.
 */
export const PROSPECT_STAGES = [
  { id: "new", label: "New", color: "#94a3b8", hint: "Not yet contacted" },
  { id: "connected", label: "Connected", color: "#7657d6", hint: "Called, still being worked" },
  // Between Connected and Appraisal (Christian Gravias, 20 Oct 2026: "can we
  // please create a category in prospects between connects & appraisals to be
  // a tab called long term").
  { id: "long_term", label: "Long Term", color: "#c026d3", hint: "Not selling soon, keep in touch" },
  { id: "appraisal", label: "Appraisal", color: "#d99018", hint: "On an appraisal board" },
  { id: "listed", label: "Listed", color: "#55C5D0", hint: "Won, not on the portals yet" },
  { id: "for_sale", label: "For Sale", color: "#2563eb", hint: "Live listings" },
  { id: "sold", label: "Sold", color: "#16a36a", hint: "Sold in the last 90 days" },
  { id: "lost", label: "Lost", color: "#e11d48", hint: "Marked lost" },
];

// Stages that are still being worked. Lost is on the board so it can be
// tracked, but it is not pipeline, so it stays out of the totals.
export const isPipelineStage = (stageId) => stageId !== "lost";

// Cold / Warm / Hot on an appraisal, in the colours of the live CRM's appraisal
// boards (blue, orange, green), so a card reads the same on both (Christian
// Gravias, Monday 12988442847, 13 Oct 2026: "Use the colours like we have on
// our current crm"). `rank` is the Hot-to-Cold sort order on the list.
export const TEMPERATURES = [
  { id: "hot", label: "Hot", rank: 3, tag: "bg-[#EBFDF2] text-[#16a34a]", strip: "bg-[#EBFDF2]", value: "text-[#22C55E]" },
  { id: "warm", label: "Warm", rank: 2, tag: "bg-[#FFF4E5] text-[#ea580c]", strip: "bg-[#FFF4E5]", value: "text-[#F97316]" },
  { id: "cold", label: "Cold", rank: 1, tag: "bg-[#E0F2FE] text-[#0284c7]", strip: "bg-[#E0F2FE]", value: "text-[#0EA5E9]" },
];
export const TEMPERATURE_BY_ID = Object.fromEntries(TEMPERATURES.map((t) => [t.id, t]));

export const EMPTY_COUNTS = Object.fromEntries(PROSPECT_STAGES.map((s) => [s.id, 0]));

const LOST_REASON_LABEL = Object.fromEntries(LOST_REASONS.map((r) => [r.value, r.label]));

export const STAGE_BY_ID = Object.fromEntries(PROSPECT_STAGES.map((s) => [s.id, s]));

export const MARKETS = [
  { id: "all", label: "All markets" },
  { id: "off_market", label: "Off market" },
  { id: "on_market", label: "On market" },
];

export const SOLD_WINDOW_DAYS = 90;

// A lead that has sat in New for longer than this is "at risk": the dashboard's
// Needs attention tile and the board's At risk tile are the same number.
export const AT_RISK_HOURS = 24;

const rowsOf = (res) => {
  const data = res?.data;
  const rows = data?.results ?? data ?? [];
  return Array.isArray(rows) ? rows : [];
};

const countOf = (res, rows) => {
  const n = Number(res?.data?.count);
  return Number.isFinite(n) ? n : rows.length;
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// The commission a property would earn: the fixed amount when one is set,
// otherwise the rate applied to the price. Same rule as the old appraisal
// board's column totals, so a figure means the same thing on both.
export const commissionOf = (p) => {
  const amount = num(p?.commission_amount);
  if (amount > 0) return amount;
  const rate = num(p?.commission_rate);
  const price = num(p?.sold_price) || num(p?.price);
  return rate > 0 && price > 0 ? (rate / 100) * price : 0;
};

export const formatMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return `$${(v / 1000000).toFixed(v >= 10000000 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}k`;
  // A dollar amount, not a date, so the Melbourne date rule does not apply.
  // eslint-disable-next-line no-restricted-syntax
  return `$${Math.round(v).toLocaleString()}`;
};

const streetOf = (p) =>
  p?.address ||
  [p?.street_number, p?.street_name].filter(Boolean).join(" ") ||
  p?.title ||
  "Untitled property";

const suburbOf = (p) => [p?.suburb, p?.state].filter(Boolean).join(" ");

// The whole address for a card: street, suburb, state, postcode. Built here
// rather than from the server's full_address, which drops a suburb whose name
// is also in the street ("118-120 Boronia Road, Boronia" came back without
// "Boronia"). A street line that already carries its suburb is left as is.
export const fullAddressOf = (p) => {
  const street = streetOf(p);
  const suburb = String(p?.suburb || "").trim();
  const tail = [suburb, p?.state, p?.postal_code].filter(Boolean).join(" ");
  if (!tail) return street;
  if (suburb && street.toLowerCase().includes(`, ${suburb.toLowerCase()}`)) return street;
  return `${street}, ${tail}`;
};

const statusOf = (p) => String(p?.status || "").toLowerCase();

// Which market a property sits in. A listing says so in its status; a sale has
// lost that (its status is now Sold), so it falls back to the rule the model
// uses when an appraisal is listed (Property._listing_status_name_on_publish):
// on a public portal means on market, otherwise the appraisal's own tag.
export const propertyMarket = (p) => {
  const status = statusOf(p);
  if (status.includes("off market")) return "off_market";
  if (status.includes("on market")) return "on_market";
  if (status === "appraisal") {
    const t = String(p?.appraisal_market_type || "").toLowerCase();
    return t === "on_market" || t === "off_market" ? t : "";
  }
  if (p?.published_on_rea || p?.rea_listing_id || p?.published_on_collings) return "on_market";
  return String(p?.appraisal_market_type || "").toLowerCase() === "on_market" ? "on_market" : "off_market";
};

const leadNotes = (lead) => (Array.isArray(lead?.agent_notes) ? lead.agent_notes : []);

const leadName = (lead) => (lead?.name || lead?.full_name || "").trim() || "No name";

const leadAddress = (lead) => lead?.property_address || lead?.owner_subject_address || "";

const agentName = (agent) => (agent && typeof agent === "object" ? agent.name || agent.email || "" : "");

const toLeadItem = (lead, stage) => {
  const notes = leadNotes(lead);
  const lastNote = notes[notes.length - 1] || null;
  const enteredAt =
    stage === "connected"
      ? lead.last_contact_date || lead.created_at
      : stage === "long_term"
      ? lead.long_term_at || lead.last_contact_date || lead.created_at
      : stage === "lost"
      ? lead.lost_at || lead.seller_action_at || lead.created_at
      : lead.created_at;
  const lostReason =
    stage === "lost" ? withNote(LOST_REASON_LABEL[lead.lost_reason] || "Lost", lead.lost_reason_note) : "";
  return {
    key: `lead-${lead.id}`,
    kind: "lead",
    stage,
    market: lead.market === "on_market" ? "on_market" : "off_market",
    title: leadName(lead),
    subtitle: leadAddress(lead),
    source: lead.source || "",
    // Sale or rental (DOMA 13375194285); rental leads get a pill on the card.
    transactionType: lead.transaction_type || "sale",
    // What the lead said about timing ("When are you thinking of selling?"),
    // shown as Selling Time Frame (Christian Gravias, Monday 13322467102,
    // 19 Oct 2026: "Change Potential to Selling Time Frame").
    timeframe: (lead.timeline || lead.owner_timeline || "").trim(),
    // Cold / Warm / Hot once the lead has been called (Christian Gravias,
    // Monday 12988442847, 16 Oct 2026: "Add a temperature for connected").
    // Long Term keeps the one it had in Connected.
    temperature: stage === "connected" || stage === "long_term" ? String(lead.temperature || "").toLowerCase() : "",
    potential: 0,
    enteredAt,
    lostReason,
    lastActivity:
      lastNote?.content ||
      (stage === "new" ? "Not yet called" : stage === "lost" ? "" : stage === "long_term" && !lead.last_contact_date ? "Long term" : "Called"),
    lastActivityAt: lastNote?.created_at || lead.last_contact_date || lead.created_at,
    assigned: lead.agent_name && lead.agent_name !== "N/A" ? lead.agent_name : "",
    // Who to ring and where, in full on every card (Christian Gravias, Monday
    // 12988442847, 1 Oct 2026: "Add Mobile & email to tile for agent to call
    // Prospect & show full address. Dont cut it off").
    person: leadName(lead),
    phone: (lead.phone || "").trim(),
    email: (lead.email || "").trim(),
    fullAddress: leadAddress(lead),
    // The Database row at this address, when the viewer may open it (DOMA 13321183498).
    databaseRecord: lead.database_record || null,
    propertyProfile: lead.property_profile || null,
    raw: lead,
  };
};

const toPropertyItem = (p, stage) => {
  const note = p?.latest_note || null;
  const enteredAt =
    stage === "sold"
      ? p.sold_date || p.updated_at
      : stage === "listed" || stage === "for_sale"
      ? p.listing_start_date || p.created_at
      : stage === "lost"
      ? p.withdrawn_at || p.updated_at
      : p.created_at;
  return {
    key: `prop-${p.id}`,
    kind: "property",
    stage,
    market: propertyMarket(p),
    title: streetOf(p),
    subtitle: suburbOf(p),
    // Carried over from the seller lead behind the appraisal, so Source and
    // Selling Time Frame stay filled after the lead moves on (Monday 13322467102).
    source: p.seller_lead_source || "",
    timeframe: (p.seller_lead_timeline || "").trim(),
    temperature: stage === "appraisal" ? String(p.appraisal_temperature || "").toLowerCase() : "",
    potential: stage === "lost" ? 0 : commissionOf(p),
    // On an appraisal lost off the board the withdrawn reason is only the
    // generic "lead_lost"; the reason the agent chose is p.lost_reason (kept on
    // the card since 26 Oct, before that only on its seller lead), so show that
    // instead of "Marked lost" (Monday 13375284987). A listing keeps its own
    // withdraw reason. Either way "Other" carries the agent's own words.
    lostReason:
      stage === "lost"
        ? withNote(
            p.withdrawn_reason === "lead_lost"
              ? LOST_REASON_LABEL[p.lost_reason] || withdrawnReasonLabel(p.withdrawn_reason)
              : withdrawnReasonLabel(p.withdrawn_reason),
            p.withdrawn_reason_note
          )
        : "",
    enteredAt,
    lastActivity: note?.content || "",
    lastActivityAt: note?.created_at || p.updated_at || p.created_at,
    assigned: agentName(p.agent),
    // The owner, so an appraisal or listing card reads like a lead card: full
    // name, mobile, email and the whole address ("Show Owner full name, mobile
    // & email and dont cut off address (make all the tabs the same)").
    person: (p?.owner?.name || "").trim(),
    phone: (p?.owner?.contact || "").trim(),
    email: (p?.owner?.email || "").trim(),
    fullAddress: fullAddressOf(p),
    raw: p,
  };
};

/**
 * Who the board is for. `agencyWide` is the principal's All agents switch
 * (useAgencyScope("pipeline")); `canScope` says whether that switch exists for
 * this user, because the seller-lead list only reads `agency_wide` when it is
 * sent and an agent's request should look exactly like it does on Seller Leads.
 */
const scopeParams = ({ agencyWide, canScope, boardAgent, team, ownOnly }) => {
  // The agent picker (Christian Gravias, Monday 12988442847, 13 Oct 2026: "Add
  // these buttons for super admin or if an agent is part of a team e.g.
  // Spiros/Paul/George to toggle agents"). One agent is the whole scope; the
  // server only honours it for the admin tier or a teammate.
  if (boardAgent) return { lead: { board_agent: boardAgent }, property: { board_agent: boardAgent } };
  // The whole team: seller leads already cover the desk, which is the team,
  // so only the property columns need asking.
  if (team) return { lead: {}, property: { team: true } };
  // A team member's "My pipeline": their own seller leads, not the whole desk.
  if (ownOnly) return { lead: { own_only: "true" }, property: {} };
  return {
    lead: canScope ? { agency_wide: agencyWide ? "true" : "false" } : {},
    property: agencyWide ? { agency_wide: true } : {},
  };
};

/**
 * Load every column of the board in one go.
 *
 * `pageSize` caps how many cards each column brings back; the column counts
 * always come from the server's own `count`, so a column with more records
 * than cards still reports its true size. The dashboard passes a small page
 * size because it only wants the numbers.
 */
export const loadProspectsPipeline = async ({
  agencyWide = false,
  canScope = false,
  boardAgent = null,
  team = false,
  ownOnly = false,
  pageSize = 100,
} = {}) => {
  const scope = scopeParams({ agencyWide, canScope, boardAgent, team, ownOnly });
  const soldSince = moment().subtract(SOLD_WINDOW_DAYS, "days").format("YYYY-MM-DD");
  const leadPage = Math.max(pageSize, 1);

  const [newRes, connectedRes, longTermRes, appraisalRes, listedRes, forSaleRes, soldRes, lostLeadRes, lostPropRes, lostListingRes] = await Promise.all([
    // A lead parked as Long Term sits in that column only, called or not.
    getLeads({ type: "seller", contacted: "false", long_term: "false", page_size: leadPage, ...scope.lead }),
    getLeads({ type: "seller", contacted: "true", long_term: "false", page_size: leadPage, ...scope.lead }),
    getLeads({ type: "seller", long_term: "true", page_size: leadPage, ...scope.lead }),
    getProperties({ appraisal_pipeline: "all", page_size: pageSize, ...scope.property }),
    // Won and in My Listings, not uploaded to the portals yet (status 'Listed').
    getProperties({ method: "listed", listing_type: "sales", page_size: pageSize, ...scope.property }),
    getProperties({ active_listings_only: true, listing_type: "sales", page_size: pageSize, ...scope.property }),
    getProperties({
      method: "sold",
      sold_since: soldSince,
      ordering: "-sold_date",
      page_size: pageSize,
      ...scope.property,
    }),
    // Lost: leads marked Lost straight off the board, and appraisals marked
    // Lost (whose own leads the server leaves out, so nothing shows twice).
    getLeads({
      type: "seller",
      actioned: "true",
      prospects_lost: "true",
      page_size: leadPage,
      ...scope.lead,
    }),
    getProperties({
      appraisal_pipeline: "all",
      lost: true,
      exclude_prospect_hidden: true,
      page_size: pageSize,
      ...scope.property,
    }),
    // And sales listings lost off the board ("Move to Lost" on a Listed or
    // For Sale card withdraws the listing). Same window as Sold, so years of
    // old withdrawn listings stay off the column.
    getProperties({
      lost_listings: true,
      listing_type: "sales",
      withdrawn_since: soldSince,
      exclude_prospect_hidden: true,
      page_size: pageSize,
      ...scope.property,
    }),
  ]);

  const newRows = rowsOf(newRes);
  const connectedRows = rowsOf(connectedRes);
  const longTermRows = rowsOf(longTermRes);
  const appraisalRows = rowsOf(appraisalRes);
  const listedRows = rowsOf(listedRes);
  const forSaleRows = rowsOf(forSaleRes);
  const soldRows = rowsOf(soldRes);
  const lostLeadRows = rowsOf(lostLeadRes);
  const lostPropRows = rowsOf(lostPropRes);
  const lostListingRows = rowsOf(lostListingRes);

  const items = [
    ...newRows.map((l) => toLeadItem(l, "new")),
    ...connectedRows.map((l) => toLeadItem(l, "connected")),
    ...longTermRows.map((l) => toLeadItem(l, "long_term")),
    ...appraisalRows.map((p) => toPropertyItem(p, "appraisal")),
    ...listedRows.map((p) => toPropertyItem(p, "listed")),
    ...forSaleRows.map((p) => toPropertyItem(p, "for_sale")),
    ...soldRows.map((p) => toPropertyItem(p, "sold")),
    ...lostPropRows.map((p) => toPropertyItem(p, "lost")),
    ...lostListingRows.map((p) => toPropertyItem(p, "lost")),
    ...lostLeadRows.map((l) => toLeadItem(l, "lost")),
  ];

  const counts = {
    new: countOf(newRes, newRows),
    connected: countOf(connectedRes, connectedRows),
    long_term: countOf(longTermRes, longTermRows),
    appraisal: countOf(appraisalRes, appraisalRows),
    listed: countOf(listedRes, listedRows),
    for_sale: countOf(forSaleRes, forSaleRows),
    sold: countOf(soldRes, soldRows),
    lost:
      countOf(lostLeadRes, lostLeadRows) +
      countOf(lostPropRes, lostPropRows) +
      countOf(lostListingRes, lostListingRows),
  };

  // The lead list's analytics block counts the not-yet-called leads that are
  // older than a day (lead_listing_views.py "New > 24h"). Read off the New
  // request, so it is the same set of leads as the New column.
  const analytics = newRes?.data?.analytics || {};
  const atRisk = Number(analytics["New > 24h"] || 0);

  return {
    items,
    counts,
    atRisk,
    total: PROSPECT_STAGES.filter((s) => isPipelineStage(s.id)).reduce((sum, s) => sum + counts[s.id], 0),
  };
};

// Whole days since a date, for "Days in stage" and the card footers.
export const daysSince = (date) => {
  if (!date) return null;
  const d = moment(date);
  return d.isValid() ? Math.max(0, moment().diff(d, "days")) : null;
};

export const isAtRisk = (item) =>
  item.stage === "new" &&
  item.enteredAt &&
  moment().diff(moment(item.enteredAt), "hours") >= AT_RISK_HOURS;

export const matchesMarket = (item, market) => {
  if (!market || market === "all") return true;
  // An appraisal nobody has tagged On or Off yet shows on both boards
  // (PropertyListingView's `unclassified` rule), so it shows under both chips.
  if (!item.market) return true;
  return item.market === market;
};

export const matchesSearch = (item, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const raw = item.raw || {};
  return [item.title, item.subtitle, item.person, item.fullAddress, item.phone, item.email, item.assigned, raw.email, raw.phone, raw.suburb]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
};
